import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  DataService,
  type MemberFilters,
  type ContributionFilters,
  type LoanFilters 
} from '../services/dataService'
import { 
  type CreateMemberRequest, 
  type UpdateMemberRequest,
  type CreateContributionRequest,
  type CreateLoanRequest,
  type LoanPaymentRequest,
  type CreateDistributionRequest
} from '../services'
import { useApiClient } from '../services/api.client'

// Query keys
export const queryKeys = {
  // Members
  members: (params?: MemberFilters) => ['members', params] as const,
  member: (id: string) => ['members', id] as const,
  
  // Contributions
  contributions: (params?: ContributionFilters) => ['contributions', params] as const,
  contribution: (id: string) => ['contributions', id] as const,
  
  // Loans
  loans: (params?: LoanFilters) => ['loans', params] as const,
  loan: (id: string) => ['loans', id] as const,
  
  // Distributions
  distributions: ['distributions'] as const,
  distribution: (id: string) => ['distributions', id] as const,
  
  // Reports
  reports: {
    contributions: ['reports', 'contributions'] as const,
    financial: ['reports', 'financial'] as const,
    loans: ['reports', 'loans'] as const,
    summary: ['reports', 'summary'] as const,
  },
  
  // Dashboard
  dashboard: ['dashboard'] as const,
  
  // Health
  health: ['health'] as const,
  version: ['version'] as const,
}

// Hook to ensure API client is initialized
function useInitializeApiClient() {
  const apiClient = useApiClient();
  return apiClient;
}

// Member hooks
export function useMembers(params?: MemberFilters) {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.members(params),
    queryFn: () => DataService.getMembers(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

export function useMember(memberId: string) {
  return useQuery({
    queryKey: queryKeys.member(memberId),
    queryFn: () => DataService.getMember(memberId),
    enabled: !!memberId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCreateMember() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (data: CreateMemberRequest) => DataService.createMember(data),
    onSuccess: () => {
      // Invalidate all member queries
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

export function useUpdateMember() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: ({ memberId, data }: { memberId: string; data: UpdateMemberRequest }) => 
      DataService.updateMember(memberId, data),
    onSuccess: (_, variables) => {
      // Invalidate specific member and all member lists
      queryClient.invalidateQueries({ queryKey: queryKeys.member(variables.memberId) })
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

export function useDeleteMember() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (memberId: string) => DataService.deleteMember(memberId),
    onSuccess: (_, memberId) => {
      // Remove from cache and invalidate all member lists
      queryClient.removeQueries({ queryKey: queryKeys.member(memberId) })
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

// Contribution hooks
export function useContributions(params?: ContributionFilters) {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.contributions(params),
    queryFn: () => DataService.getContributions(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useContribution(id: string) {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.contribution(id),
    queryFn: () => DataService.getContribution(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCreateContribution() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (data: CreateContributionRequest) => DataService.createContribution(data),
    onSuccess: () => {
      // Invalidate all contribution queries
      queryClient.invalidateQueries({ queryKey: ['contributions'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

export function useConfirmContribution() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (id: string) => DataService.confirmContribution(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contribution(id) })
      queryClient.invalidateQueries({ queryKey: ['contributions'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

export function useUpdateContribution() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateContributionRequest }) => 
      DataService.updateContribution(id, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.contribution(variables.id) })
      void queryClient.invalidateQueries({ queryKey: ['contributions'] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

export function useDeleteContribution() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (id: string) => DataService.deleteContribution(id),
    onSuccess: (_, id) => {
      void queryClient.removeQueries({ queryKey: queryKeys.contribution(id) })
      void queryClient.invalidateQueries({ queryKey: ['contributions'] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

// Loan hooks
export function useLoans(params?: LoanFilters) {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.loans(params),
    queryFn: () => DataService.getLoans(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useLoan(loanId: string) {
  return useQuery({
    queryKey: queryKeys.loan(loanId),
    queryFn: () => DataService.getLoan(loanId),
    enabled: !!loanId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCreateLoan() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (data: CreateLoanRequest) => DataService.createLoan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

export function useApproveLoan() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (loanId: string) => DataService.approveLoan(loanId),
    onSuccess: (_, loanId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loan(loanId) })
      queryClient.invalidateQueries({ queryKey: ['loans'] })
    },
  })
}

export function useRejectLoan() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (loanId: string) => DataService.rejectLoan(loanId),
    onSuccess: (_, loanId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loan(loanId) })
      queryClient.invalidateQueries({ queryKey: ['loans'] })
    },
  })
}

export function useRecordLoanPayment() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: ({ loanId, data }: { loanId: string; data: LoanPaymentRequest }) => 
      DataService.recordLoanPayment(loanId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loan(variables.loanId) })
      queryClient.invalidateQueries({ queryKey: ['loans'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

// Distribution hooks
export function useDistributions() {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.distributions,
    queryFn: () => DataService.getDistributions(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useDistribution(id: string) {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.distribution(id),
    queryFn: () => DataService.getDistribution(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCreateDistribution() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (data: CreateDistributionRequest) => DataService.createDistribution(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.distributions })
    },
  })
}

export function useApproveDistribution() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (distributionId: string) => DataService.approveDistribution(distributionId),
    onSuccess: (_, distributionId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.distribution(distributionId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.distributions })
    },
  })
}

export function useExecuteDistribution() {
  const queryClient = useQueryClient()
  useInitializeApiClient();
  
  return useMutation({
    mutationFn: (distributionId: string) => DataService.executeDistribution(distributionId),
    onSuccess: (_, distributionId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.distribution(distributionId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.distributions })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

// Report hooks
export function useContributionReport() {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.reports.contributions,
    queryFn: () => DataService.getContributionReport(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useFinancialReport() {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.reports.financial,
    queryFn: () => DataService.getFinancialReport(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useLoanReport() {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.reports.loans,
    queryFn: () => DataService.getLoanReport(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useSummaryReport() {
  useInitializeApiClient();
  
  return useQuery({
    queryKey: queryKeys.reports.summary,
    queryFn: () => DataService.getSummaryReport(),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

// Dashboard hooks
export function useDashboardStats() {
  const { data: summaryReport } = useSummaryReport();
  
  return {
    data: summaryReport ? {
      success: true,
      data: summaryReport
    } : undefined,
    isLoading: !summaryReport,
    error: null
  }
}

// Health hooks
export function useHealthCheck() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => DataService.healthCheck(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Refetch every minute
  })
}

export function useVersion() {
  return useQuery({
    queryKey: queryKeys.version,
    queryFn: () => DataService.getVersion(),
    staleTime: Infinity, // Version rarely changes
    gcTime: Infinity,
  })
}

// Utility hooks
export function useIsMockMode(): boolean {
  return DataService.isMockMode()
}

// Custom hook for clearing mock data (only works in mock mode)
export function useClearMockData() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: () => {
      DataService.clearMockData()
      return Promise.resolve()
    },
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear()
    },
  })
}

// Custom hook for resetting mock data (only works in mock mode)
export function useResetMockData() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: () => {
      DataService.resetMockData()
      return Promise.resolve()
    },
    onSuccess: () => {
      // Clear all cached data to force refetch
      queryClient.clear()
    },
  })
}